import { useEffect, useRef, useState } from 'react';

class ReadingSoundManager {
  constructor() {
    this.audioContext = null;
    this.sounds = new Map();
    this.isInitialized = false;
    this.soundBuffers = new Map();
    this.activeSounds = []; // Track active sounds for stopping
  }

  async initialize() {
    if (this.isInitialized) return;
    
    try {
      // Create AudioContext for zero-latency playback
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Preload all sound effects
      await this.preloadSounds();
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize sound manager:', error);
    }
  }

  async preloadSounds() {
    const soundLibrary = {
      // Environmental sounds for background atmosphere
      'forest_ambience': '/static/audio/reading_sounds/environmental/environmental_forest_ambience_forest park october 1011 221030_0539.wav_664310.mp3',
      'ocean_waves': '/static/audio/reading_sounds/environmental/environmental_ocean_waves_Lapping Waves and Sea Gulls 2.wav_43779.mp3',
      
      // Weather sounds for dramatic scenes
      'storm_rain': '/static/audio/reading_sounds/weather/weather_storm_rain_Grzmot _3_.wav_56371.mp3',
      'storm_wind': '/static/audio/reading_sounds/weather/weather_storm_wind_Strong Wind Recorded with Soundfield Microphone Manchester 2_376534.mp3',
      
      // Action sounds for story events
      'fire_crackling': '/static/audio/reading_sounds/action/action_fire_crackling_campfire sound_541572.mp3',
      'axe_chop': '/static/audio/reading_sounds/action/action_axe_chop_Axe Wood Chopping_553391.mp3',
      'footsteps_grass': '/static/audio/reading_sounds/action/action_footsteps_grass_Women walking on grass outside with sneakers_707915.mp3',
      'door_creak': '/static/audio/reading_sounds/action/action_door_creak_Big door creak.wav_273466.mp3',
      
      // Transition sounds for story flow
      'page_turn': '/static/audio/reading_sounds/transitions/transitions_page_turn_Page_Turn_24.wav_20258.mp3'
    };

    const loadPromises = Object.entries(soundLibrary).map(async ([key, path]) => {
      try {
        const response = await fetch(path);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
        this.soundBuffers.set(key, audioBuffer);
      } catch (error) {
        console.warn(`Failed to load sound ${key}:`, error);
      }
    });

    await Promise.all(loadPromises);
  }

  playSound(soundKey, options = {}) {
    if (!this.isInitialized || !this.soundBuffers.has(soundKey)) {
      console.warn(`⚠️ Sound not available: ${soundKey}`);
      return null;
    }

    try {
      const audioBuffer = this.soundBuffers.get(soundKey);
      const source = this.audioContext.createBufferSource();
      const gainNode = this.audioContext.createGain();

      source.buffer = audioBuffer;
      
      // Configure options
      const volume = options.volume || 0.5;
      const loop = options.loop || false;
      const fadeIn = options.fadeIn || false;

      gainNode.gain.value = fadeIn ? 0 : volume;
      source.loop = loop;

      // Connect audio graph
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Handle fade in
      if (fadeIn) {
        gainNode.gain.linearRampToValueAtTime(volume, this.audioContext.currentTime + 1);
      }

      source.start();

      // Return control object for stopping/fading
      const soundControl = {
        stop: () => {
          source.stop();
          this.activeSounds = this.activeSounds.filter(s => s !== soundControl);
        },
        fadeOut: (duration = 1) => {
          gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + duration);
          setTimeout(() => {
            source.stop();
            this.activeSounds = this.activeSounds.filter(s => s !== soundControl);
          }, duration * 1000);
        }
      };
      
      // Track this sound
      this.activeSounds.push(soundControl);
      
      return soundControl;
    } catch (error) {
      console.error(`❌ Error playing sound ${soundKey}:`, error);
      return null;
    }
  }

  // Reading-specific sound effect triggers
  triggerStoryAtmosphere(sceneType) {
    const atmosphereMap = {
      'forest': 'forest_ambience',
      'wilderness': 'forest_ambience', 
      'ocean': 'ocean_waves',
      'beach': 'ocean_waves',
      'storm': 'storm_rain',
      'windy': 'storm_wind'
    };

    const soundKey = atmosphereMap[sceneType.toLowerCase()];
    if (soundKey) {
      return this.playSound(soundKey, { volume: 0.3, loop: true, fadeIn: true });
    }
  }

  triggerActionSound(actionType) {
    const actionMap = {
      'fire': 'fire_crackling',
      'campfire': 'fire_crackling',
      'chop': 'axe_chop',
      'axe': 'axe_chop',
      'walking': 'footsteps_grass',
      'footsteps': 'footsteps_grass',
      'door': 'door_creak',
      'opening': 'door_creak'
    };

    const soundKey = actionMap[actionType.toLowerCase()];
    if (soundKey) {
      return this.playSound(soundKey, { volume: 0.6 });
    }
  }

  triggerTransition(transitionType) {
    const transitionMap = {
      'page': 'page_turn',
      'chapter': 'page_turn',
      'scene': 'page_turn'
    };

    const soundKey = transitionMap[transitionType.toLowerCase()];
    if (soundKey) {
      return this.playSound(soundKey, { volume: 0.4 });
    }
  }

  stopAllSounds() {
    this.activeSounds.forEach(soundControl => {
      try {
        soundControl.stop();
      } catch (error) {
        console.warn('Error stopping sound:', error);
      }
    });
    this.activeSounds = [];
  }
}

// React Hook for using the sound manager
export const useReadingSounds = () => {
  const soundManagerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const initSoundManager = async () => {
      if (!soundManagerRef.current) {
        soundManagerRef.current = new ReadingSoundManager();
        await soundManagerRef.current.initialize();
        setIsReady(true);
      }
    };

    initSoundManager();

    return () => {
      // Cleanup would go here
    };
  }, []);

  return {
    soundManager: soundManagerRef.current,
    isReady,
    triggerStoryAtmosphere: (sceneType) => soundManagerRef.current?.triggerStoryAtmosphere(sceneType),
    triggerActionSound: (actionType) => soundManagerRef.current?.triggerActionSound(actionType),
    triggerTransition: (transitionType) => soundManagerRef.current?.triggerTransition(transitionType),
    stopAllSounds: () => soundManagerRef.current?.stopAllSounds()
  };
};

export default ReadingSoundManager;